/* Atelier Portfolio — single-column lookbook of large rounded
   portraits, each captioned in italic serif. Reads like a printed
   atelier catalog, not a gallery wall. */

import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getFocalStyle } from "@/lib/focal";
import { optimizeImageUrl } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

export default function AtelierPortfolio() {
  const { settings, loading } = useSiteSettings();
  const port = settings?.portfolio_images || [];
  const focals = settings?.portfolio_focal_points || [];
  const studioName = settings?.studio_name || "Studio";
  useEffect(() => { document.title = `${studioName} — Lookbook`; }, [studioName]);
  if (loading && !settings) return <EditorialSkeleton eyebrow="Pressing the lookbook." />;

  return (
    <div data-testid="atelier-portfolio" style={{ background: "var(--tenant-bg, #FEF5F2)", color: "var(--tenant-fg, #3A1B22)" }}>
      <section className="px-6 lg:px-12 pt-20 pb-12 text-center">
        <div className="text-[10px] uppercase tracking-[0.42em] mb-5" style={{ color: "var(--tenant-accent)" }}>
          {settings?.portfolio_eyebrow || "The Lookbook"}
        </div>
        <h1 className="italic" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
          {settings?.portfolio_title || "Recent sittings."}
        </h1>
        <p className="mt-6 max-w-md mx-auto italic text-base leading-loose" style={{ color: "var(--tenant-fg-soft)" }}>
          A small selection. Hand-picked, hand-pressed, hand-delivered.
        </p>
      </section>

      <div className="max-w-2xl mx-auto px-6 pb-32 space-y-16">
        {port.map((src, i) => (
          <figure key={i}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[28px] border opacity-50" style={{ borderColor: "var(--tenant-accent)" }} aria-hidden />
              <img src={optimizeImageUrl(src, { w: 1400 })} alt=""
                className="relative w-full aspect-[4/5] object-cover rounded-[20px]"
                style={getFocalStyle(focals, i)}
                loading={i < 2 ? "eager" : "lazy"} decoding="async" />
            </div>
            <figcaption className="text-center mt-6 italic text-base"
                        style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft)" }}>
              Sitting №{String(i + 1).padStart(2, "0")} · {studioName}
            </figcaption>
          </figure>
        ))}
        {port.length === 0 && (
          <p className="text-center italic" style={{ color: "var(--tenant-fg-soft)" }}>
            The lookbook is being pressed. Check back soon.
          </p>
        )}
      </div>
    </div>
  );
}
